import { useEffect, useState, useCallback, useRef } from 'react';
import { Awareness } from 'y-protocols/awareness';

export interface CollaboratorPresence {
    clientId: number;
    user: {
        userId: string;
        displayName: string;
        color: string;
        avatarUrl: string | null;
    };
    cursor?: { x: number; y: number };
    selectedLegacyIds?: number[];
}

/**
 * Subscribe to awareness state from all peers (excluding the local user).
 *
 * Returns a list of `CollaboratorPresence` entries that re-renders whenever
 * any peer's awareness state changes. Local awareness updates are filtered out
 * because the calling component is the one driving them.
 */
export function useAwareness(awareness: Awareness | null): CollaboratorPresence[] {
    const [peers, setPeers] = useState<CollaboratorPresence[]>([]);

    useEffect(() => {
        if (!awareness) {
            setPeers([]);
            return;
        }

        const refresh = () => {
            const collected: CollaboratorPresence[] = [];
            for (const [clientId, state] of awareness.getStates()) {
                if (clientId === awareness.clientID) continue;
                if (!state || !state.user) continue;
                collected.push({
                    clientId,
                    user: state.user as CollaboratorPresence['user'],
                    cursor: state.cursor as CollaboratorPresence['cursor'],
                    selectedLegacyIds: state.selectedLegacyIds as number[] | undefined,
                });
            }
            setPeers(collected);
        };

        refresh();
        awareness.on('change', refresh);
        return () => {
            awareness.off('change', refresh);
        };
    }, [awareness]);

    return peers;
}

/**
 * Throttled cursor publisher. Returns a function that updates the local
 * awareness `cursor` field at most once per `intervalMs`.
 */
export function useCursorPublisher(awareness: Awareness | null, intervalMs = 50): (x: number, y: number) => void {
    const pendingRef = useRef<{ x: number; y: number } | null>(null);
    const timerRef = useRef<number | null>(null);

    return useCallback(
        (x: number, y: number) => {
            if (!awareness) return;
            pendingRef.current = { x, y };
            if (timerRef.current !== null) return;
            timerRef.current = window.setTimeout(() => {
                timerRef.current = null;
                const next = pendingRef.current;
                pendingRef.current = null;
                if (next) {
                    awareness.setLocalStateField('cursor', next);
                }
            }, intervalMs);
        },
        [awareness, intervalMs],
    );
}
